// ShareGroupModal.js - Modal for adding resources to sharing groups
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { 
  getUserSharingGroups, 
  createSharingGroup, 
  addResourcesToSharingGroup,
  getSharingGroupUrl
} from '../services/sharingGroups';
import { FaTimes, FaCopy, FaPlus } from 'react-icons/fa';

const ShareGroupModal = ({ 
  isOpen, 
  onClose, 
  resources = [], // Array of { type: 'run'|'racer'|'group', id: string, name: string }
  onShareComplete = null 
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [sharingGroups, setSharingGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupPermission, setNewGroupPermission] = useState('read');
  const [newGroupIsPublic, setNewGroupIsPublic] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);

  // Load user's sharing groups
  useEffect(() => {
    if (isOpen && user && !user.isAnonymous) {
      loadSharingGroups();
    }
  }, [isOpen, user]);

  const loadSharingGroups = async () => {
    try {
      const groups = await getUserSharingGroups(user.uid);
      setSharingGroups(groups);
    } catch (error) {
      console.error('Error loading sharing groups:', error);
      showToast('Failed to load sharing groups.');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      showToast('Please enter a group name.');
      return;
    }

    setIsCreating(true);
    try {
      const newGroup = await createSharingGroup({
        ownerId: user.uid,
        name: newGroupName.trim(),
        permission: newGroupPermission,
        isPublic: newGroupIsPublic,
        allowedUsers: []
      });

      await loadSharingGroups();
      setSelectedGroupId(newGroup.id);
      setShowCreateForm(false);
      setNewGroupName('');
      setNewGroupPermission('read');
      setNewGroupIsPublic(false);
      showToast('Sharing group created successfully!');
    } catch (error) {
      console.error('Error creating sharing group:', error);
      showToast('Failed to create sharing group. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddToGroup = async () => {
    if (!selectedGroupId) {
      showToast('Please select a sharing group.');
      return;
    }

    if (resources.length === 0) {
      showToast('No resources to add.');
      return;
    }

    setIsSharing(true);
    try {
      await addResourcesToSharingGroup(selectedGroupId, resources);
      
      // Get the group to show URL if public
      const group = sharingGroups.find(g => g.id === selectedGroupId);
      if (group && group.isPublic) {
        const url = getSharingGroupUrl(group);
        setGeneratedUrl(url);
      }
      
      showToast(`Successfully added ${resources.length} resource(s) to sharing group!`);
      
      if (onShareComplete) {
        onShareComplete(selectedGroupId);
      }
    } catch (error) {
      console.error('Error adding resources to sharing group:', error);
      showToast('Failed to add resources. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      showToast('URL copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      showToast('Failed to copy URL.');
    });
  };

  const handleClose = () => {
    setSelectedGroupId('');
    setShowCreateForm(false);
    setNewGroupName('');
    setNewGroupPermission('read');
    setNewGroupIsPublic(false);
    setGeneratedUrl(null);
    onClose();
  };

  if (!isOpen) return null;

  const selectedGroup = sharingGroups.find(g => g.id === selectedGroupId);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0 }}>Add to Sharing Group</h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px',
              lineHeight: 1
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {generatedUrl ? (
            /* Share URL Generated */
            <div>
              <div style={{
                padding: '15px',
                backgroundColor: '#d4edda',
                borderRadius: '4px',
                marginBottom: '20px',
                color: '#155724'
              }}>
                <strong>Resources added successfully!</strong>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px' }}>
                  Public Share URL:
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: '#f8f9fa',
                  padding: '8px',
                  borderRadius: '4px'
                }}>
                  <input
                    type="text"
                    value={generatedUrl}
                    readOnly
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      fontSize: '0.9em',
                      color: '#333'
                    }}
                  />
                  <button
                    onClick={() => handleCopyUrl(generatedUrl)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#13A1A8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '0.85em'
                    }}
                  >
                    <FaCopy /> Copy
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Resources Summary */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px' }}>
                  Resources to Add:
                </label>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#f9f9f9'
                }}>
                  {resources.map((resource, index) => (
                    <div key={`${resource.type}-${resource.id}`} style={{
                      padding: '8px',
                      borderBottom: index < resources.length - 1 ? '1px solid #eee' : 'none'
                    }}>
                      <div style={{ fontWeight: 'medium' }}>{resource.name}</div>
                      <div style={{ fontSize: '0.85em', color: '#666', textTransform: 'capitalize' }}>
                        {resource.type}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Select or Create Group */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px' }}>
                  Select Sharing Group:
                </label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">-- Select a sharing group --</option>
                    {sharingGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.permission === 'read' ? 'Read' : 'Write'})
                        {group.isPublic ? ' - Public' : ' - Private'}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#13A1A8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FaPlus /> New Group
                  </button>
                </div>

                {/* Create New Group Form */}
                {showCreateForm && (
                  <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '15px',
                    backgroundColor: '#f9f9f9',
                    marginTop: '10px'
                  }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Create New Sharing Group</h3>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                        Group Name:
                      </label>
                      <input
                        type="text"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        placeholder="e.g., Shared with Coach John"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px' }}>
                        Permission:
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            value="read"
                            checked={newGroupPermission === 'read'}
                            onChange={(e) => setNewGroupPermission(e.target.value)}
                            style={{ marginRight: '8px' }}
                          />
                          <div>
                            <div style={{ fontWeight: 'medium' }}>Read</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>
                              Recipients can view content but cannot edit
                            </div>
                          </div>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            value="write"
                            checked={newGroupPermission === 'write'}
                            onChange={(e) => setNewGroupPermission(e.target.value)}
                            style={{ marginRight: '8px' }}
                          />
                          <div>
                            <div style={{ fontWeight: 'medium' }}>Write</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>
                              Recipients can view and edit content
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={newGroupIsPublic}
                          onChange={(e) => setNewGroupIsPublic(e.target.checked)}
                          style={{ marginRight: '8px' }}
                        />
                        <div>
                          <div style={{ fontWeight: 'medium' }}>Public URL</div>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            Generate a public URL that anyone can access
                          </div>
                        </div>
                      </label>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewGroupName('');
                          setNewGroupPermission('read');
                          setNewGroupIsPublic(false);
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#ccc',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateGroup}
                        disabled={isCreating || !newGroupName.trim()}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: isCreating || !newGroupName.trim() ? '#ccc' : '#13A1A8',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: isCreating || !newGroupName.trim() ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isCreating ? 'Creating...' : 'Create Group'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px'
        }}>
          {generatedUrl ? (
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#13A1A8',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'medium'
              }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ccc',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'medium'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddToGroup}
                disabled={isSharing || !selectedGroupId}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isSharing || !selectedGroupId ? '#ccc' : '#13A1A8',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSharing || !selectedGroupId ? 'not-allowed' : 'pointer',
                  fontWeight: 'medium'
                }}
              >
                {isSharing ? 'Adding...' : 'Add to Group'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareGroupModal;
